import {
  Button,
  Col,
  Icon,
  Select,
  SettingRow,
  Stack,
  Switch,
  Tappable,
  Text,
  usePrefersReducedMotion,
  useTheme,
} from "@rally/ui-kit";
import { useCallback, useState } from "react";
import { say } from "../localization/say.ts";
import { CUES, type Cue, voiceFor } from "../sound/cue.ts";
import {
  keepSoundSettings,
  LOUDNESSES,
  type Loudness,
  MASTERS,
  type Master,
  type SoundSettings,
  soundSettingsFor,
} from "../sound/settings.ts";
import { previewOf, readTheSettings, settingsNow } from "../sound/sounding.ts";

const cueNames = (): Record<Cue, string> => ({
  message: say("sound.cue.message"),
  mention: say("sound.cue.mention"),
  agentDone: say("sound.cue.agentDone"),
  agentNeedsYou: say("sound.cue.agentNeedsYou"),
  ringing: say("sound.cue.ringing"),
  youJoined: say("sound.cue.youJoined"),
  somebodyJoined: say("sound.cue.somebodyJoined"),
  somebodyLeft: say("sound.cue.somebodyLeft"),
  callEnded: say("sound.cue.callEnded"),
  recordingStarted: say("sound.cue.recordingStarted"),
  sendFailed: say("sound.cue.sendFailed"),
  summary: say("sound.cue.summary"),
});

const masterWords = (): Record<Master, string> => ({
  on: say("sound.on"),
  mentions: say("sound.mentions"),
  off: say("sound.off"),
});

const loudnessWords = (): Record<Loudness, string> => ({
  soft: say("sound.soft"),
  normal: say("sound.normal"),
  loud: say("sound.loud"),
});

function Preview({ cue }: { cue: Cue }) {
  const theme = useTheme();
  return (
    <Col style={{ flexDirection: "row", alignItems: "center", gap: theme.space[2] }}>
      <Text typeName="caption" color="text.secondary" style={{ flex: 1 }} numberOfLines={1}>
        {cueNames()[cue]}
      </Text>
      <Tappable
        testID={`sound-preview-${cue}`}
        accessibilityRole="button"
        accessibilityLabel={say("sound.hear")(cueNames()[cue])}
        onPress={() => previewOf(cue, voiceFor(cue, null))}
        style={{
          width: 32,
          height: 32,
          alignItems: "center",
          justifyContent: "center",
          borderRadius: theme.radius.round,
        }}
      >
        <Icon name="play" size={14} color="text.secondary" />
      </Tappable>
    </Col>
  );
}

export function SoundSetting({ me }: { me: string }) {
  const theme = useTheme();
  const reduced = usePrefersReducedMotion();

  const [chosen, setChosen] = useState<SoundSettings | null>(() => soundSettingsFor(me));
  const [previews, setPreviews] = useState(false);
  const settings = chosen ?? settingsNow();

  const change = useCallback(
    (next: SoundSettings) => {
      keepSoundSettings(me, next);
      setChosen(next);
      readTheSettings();
    },
    [me],
  );

  return (
    <Col testID="sound-setting">
      <SettingRow
        title={say("sound.master")}
        note={chosen === null && reduced ? say("sound.quiet") : say("sound.what")}
        control={
          <Col testID="sound-master">
            <Select
              value={settings.master}
              options={MASTERS.map((master) => ({ label: masterWords()[master], value: master }))}
              onChange={(master) => change({ ...settings, master: master as Master })}
            />
          </Col>
        }
      />
      <SettingRow
        title={say("sound.calls")}
        control={
          <Col style={{ alignItems: "flex-end" }}>
            <Switch
              value={settings.calls}
              labelSide="hidden"
              label={say("sound.calls")}
              onChange={(calls) => change({ ...settings, calls })}
            />
          </Col>
        }
      />
      <SettingRow
        title={say("sound.loudness")}
        control={
          <Col testID="sound-loudness">
            <Select
              value={settings.loudness}
              options={LOUDNESSES.map((one) => ({ label: loudnessWords()[one], value: one }))}
              onChange={(loudness) => change({ ...settings, loudness: loudness as Loudness })}
            />
          </Col>
        }
      />
      <SettingRow
        title={say("sound.preview")}
        last
        control={
          <Col style={{ alignItems: "flex-end" }}>
            <Button
              size="sm"
              variant="ghost"
              icon={previews ? "chevron-down" : "chevron-right"}
              onPress={() => setPreviews(!previews)}
              testID="sound-previews"
            >
              {previews ? say("sound.hidePreviews") : say("sound.showPreviews")}
            </Button>
          </Col>
        }
      />
      {previews ? (
        <Stack gap={1} style={{ paddingTop: theme.space[2] }}>
          <Text typeName="caption" color="text.tertiary">
            {say("sound.previewAnyway")}
          </Text>
          {CUES.map((cue) => (
            <Preview key={cue} cue={cue} />
          ))}
        </Stack>
      ) : null}
    </Col>
  );
}
